import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';

function Icon({ name, size = 20, style }) {
  return <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...style }}>{name}</span>;
}

function pad(n) { return String(n).padStart(2, '0'); }
function toIsoDate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

function getEventColor(event) {
  if (event.type === 'inbound') return { bg: '#dcfce7', bar: '#009844', label: '입고' };
  if (event.type === 'cancel') return { bg: '#fff4e5', bar: '#e65100', label: '취소' };
  if (event.type === 'return') return { bg: '#f3e5f5', bar: '#6a1b9a', label: '환불' };
  // 출고요청(REQ-) — note에서 상태 확인
  if (event.serial && event.serial.startsWith('REQ-')) {
    let noteObj = {};
    try { noteObj = JSON.parse(event.note || '{}'); } catch(e){}
    if (noteObj.processedAs === 'cancel') return { bg: '#fff4e5', bar: '#e65100', label: '취소' };
    if (noteObj.processedAs === 'return') return { bg: '#f3e5f5', bar: '#6a1b9a', label: '환불' };
    // RMA/DOA — note.rma flag로 구분 (emsType: 'RMA' or 'DOA')
    if (noteObj.rma) {
      const emsLabel = noteObj.emsType || 'RMA';
      if (noteObj.repaired) return { bg: '#e0f2f1', bar: '#00695c', label: emsLabel + ' 수리완료' };
      return { bg: '#fbe9e7', bar: '#d84315', label: emsLabel };
    }
    if (noteObj.fulfilled) return { bg: '#dcfce7', bar: '#009844', label: '출고완료' };
    return { bg: '#dce9ff', bar: '#00668a', label: '출고요청' };
  }
  return { bg: '#ffebee', bar: '#ba1a1a', label: '출고' };
}

// v7.16: outbound 이벤트의 카테고리 판별 (rma 분리용)
function getOutboundCategory(event) {
  if (event.serial && event.serial.startsWith('REQ-')) {
    let noteObj = {};
    try { noteObj = JSON.parse(event.note || '{}'); } catch(e){}
    if (noteObj.rma) return 'rma';
  }
  return 'outbound';
}

// v7.188: 이벤트에서 items 리스트 추출 — 여러 품목이 있는 tx(출고요청/입고)에서
//         각 품목의 제품명 + 수량을 캘린더/디테일에 표기하기 위함
//         반환: [{ name, qty }] · items 없으면 fallback으로 ev.model 하나 (qty=null)
function getEventItems(ev) {
  try {
    const p = JSON.parse(ev.note || '{}');
    if (Array.isArray(p.items) && p.items.length > 0) {
      return p.items.map(it => ({
        name: it.product_name || '-',
        qty: typeof it.qty === 'number' ? it.qty : (Array.isArray(it.serials) ? it.serials.length : null),
      }));
    }
  } catch (e) {}
  return [{ name: ev.model || '-', qty: null }];
}
// 총 수량 (items 배열의 qty 합계 · null은 1로 취급)
function getEventTotalQty(items) {
  return items.reduce((s, it) => s + (it.qty != null ? it.qty : 1), 0);
}

const CELL_HEIGHT = 130;          // 각 셀 고정 높이 (px)
const CALENDAR_WIDTH = 1240;      // 캘린더 그리드 영역 고정 폭 (px)
const DETAIL_WIDTH = 360;         // 디테일 패널 폭 (px)

export default function EventCalendar({ open, onClose, onPickDate }) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [eventsByDate, setEventsByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Outlook 스타일 연/월 피커
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(month.getFullYear());
  const labelRef = useRef(null);

  // 이벤트 타입 필터 (다중 토글) — v7.16: 'rma' 추가
  const [activeTypes, setActiveTypes] = useState(() => new Set(['inbound', 'outbound', 'rma', 'cancel', 'return']));
  const toggleType = (t) => setActiveTypes(prev => {
    const next = new Set(prev);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });
  const resetTypes = () => setActiveTypes(new Set(['inbound', 'outbound', 'rma', 'cancel', 'return']));
  // 이벤트가 현재 활성 필터에 부합하는지
  const isEventActive = (ev) => {
    if (ev.type === 'inbound') return activeTypes.has('inbound');
    if (ev.type === 'cancel') return activeTypes.has('cancel');
    if (ev.type === 'return') return activeTypes.has('return');
    // outbound — REQ-의 rma 여부에 따라 분기
    const cat = getOutboundCategory(ev);
    return activeTypes.has(cat);
  };

  // 외부 month 변경 시 pickerYear도 동기화
  useEffect(() => { setPickerYear(month.getFullYear()); }, [month]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    const start = new Date(month.getFullYear(), month.getMonth(), 1);
    const end = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const startStr = toIsoDate(start);
    const endStr = toIsoDate(end);

    const { data } = await supabase.from('transaction_log')
      .select('id,serial,model,type,dealer_name,note,transaction_date,created_at')
      .gte('transaction_date', startStr)
      .lte('transaction_date', endStr)
      .order('created_at', { ascending: true })
      .limit(2000);

    const grouped = {};
    (data || []).forEach(row => {
      const d = row.transaction_date;
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(row);
    });
    setEventsByDate(grouped);
    setLoading(false);
  }, [month]);

  useEffect(() => {
    if (open) loadEvents();
  }, [open, loadEvents]);

  // 모달 닫힐 때 상태 초기화
  useEffect(() => {
    if (!open) { setSelectedDate(null); setPickerOpen(false); }
  }, [open]);

  // ESC 키로 닫기
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (pickerOpen) setPickerOpen(false);
        else if (selectedDate) setSelectedDate(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, pickerOpen, selectedDate, onClose]);

  if (!open) return null;

  // 캘린더 셀 생성
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const startDayOfWeek = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const cells = [];
  const prevMonthLastDay = new Date(month.getFullYear(), month.getMonth(), 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    cells.push({ date: prevMonthLastDay - i, inCurrentMonth: false, fullDate: null });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    const fullDate = `${month.getFullYear()}-${pad(month.getMonth() + 1)}-${pad(i)}`;
    cells.push({ date: i, inCurrentMonth: true, fullDate });
  }
  // 항상 6주(42셀) 고정 — 그리드 높이 일관성 유지
  const totalCells = 42;
  let nextDay = 1;
  while (cells.length < totalCells) {
    cells.push({ date: nextDay, inCurrentMonth: false, fullDate: null });
    nextDay++;
  }

  const today = new Date();
  const todayStr = toIsoDate(today);
  const monthLabel = `${month.getFullYear()}년 ${month.getMonth() + 1}월`;

  const prevMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const nextMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  const goToday = () => setMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  const selectMonth = (year, monthIdx) => { setMonth(new Date(year, monthIdx, 1)); setPickerOpen(false); };

  const handleDayClick = (cell) => {
    if (!cell.inCurrentMonth || !cell.fullDate) return;
    // 항상 디테일 패널 토글 (재고 목록과 동일한 UX)
    // onPickDate가 있는 경우(배송시트지) 패널 내부에 별도 "이 날짜로 보기" 버튼 제공
    setSelectedDate(s => s === cell.fullDate ? null : cell.fullDate);
  };

  const selectedDateEvents = selectedDate ? (eventsByDate[selectedDate] || []).filter(isEventActive) : [];

  const monthlyStats = Object.values(eventsByDate).flat().reduce((acc, ev) => {
    if (ev.type === 'inbound') acc.inbound++;
    else if (ev.type === 'cancel') acc.cancel++;
    else if (ev.type === 'return') acc.return++;
    else if (ev.type === 'outbound') {
      // v7.16: outbound 중 rma 분리
      if (getOutboundCategory(ev) === 'rma') acc.rma++;
      else acc.outbound++;
    }
    return acc;
  }, { inbound: 0, outbound: 0, rma: 0, cancel: 0, return: 0 });
  const totalCount = monthlyStats.inbound + monthlyStats.outbound + monthlyStats.rma + monthlyStats.cancel + monthlyStats.return;
  // 현재 활성 필터 기준 가시 카운트
  const visibleCount = (activeTypes.has('inbound') ? monthlyStats.inbound : 0)
    + (activeTypes.has('outbound') ? monthlyStats.outbound : 0)
    + (activeTypes.has('rma') ? monthlyStats.rma : 0)
    + (activeTypes.has('cancel') ? monthlyStats.cancel : 0)
    + (activeTypes.has('return') ? monthlyStats.return : 0);
  const allActive = activeTypes.size === 5;

  // 모달 폭: 디테일 패널 여부와 상관없이 캘린더 폭은 고정
  // 디테일이 열리면 모달 전체가 옆으로 커짐 (캘린더는 그대로)
  const modalWidth = selectedDate ? (CALENDAR_WIDTH + DETAIL_WIDTH + 32) : CALENDAR_WIDTH + 32;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20, backdropFilter: 'blur(2px)' }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16,
        width: modalWidth, maxWidth: '95vw',
        height: 'auto', maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        transition: 'width 0.2s ease',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name="calendar_month" size={24} style={{ color: '#00668a' }} />
            <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0, color: '#0b1c30' }}>입출고 이벤트 캘린더</h2>
            <span style={{ fontSize: 12, color: '#76777d', marginLeft: 4 }}>· {allActive ? `${totalCount}건` : `${visibleCount} / ${totalCount}건`}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={goToday} style={{ padding: '6px 14px', border: '1px solid #c6c6cd', borderRadius: 8, background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#45464d' }}>오늘</button>
            <button onClick={prevMonth} style={{ width: 32, height: 32, border: '1px solid #c6c6cd', borderRadius: 8, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><Icon name="chevron_left" size={18} /></button>

            {/* Outlook 스타일 연/월 라벨 + 피커 */}
            <div ref={labelRef} style={{ position: 'relative' }}>
              <button onClick={() => setPickerOpen(v => !v)}
                style={{ padding: '6px 14px', minWidth: 130, textAlign: 'center', border: `1px solid ${pickerOpen ? '#00668a' : 'transparent'}`, borderRadius: 8, background: pickerOpen ? '#e5eeff' : 'transparent', fontSize: 16, fontWeight: 600, color: '#0b1c30', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                {monthLabel}
                <Icon name={pickerOpen ? 'expand_less' : 'expand_more'} size={16} style={{ color: '#76777d' }} />
              </button>
              {pickerOpen && (
                <>
                  {/* 클릭 외 영역 닫기 백드롭 */}
                  <div onClick={() => setPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
                  {/* 피커 팝업 */}
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
                    background: '#fff', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    padding: 14, zIndex: 20, minWidth: 280,
                    border: '1px solid #E2E8F0'
                  }}>
                    {/* 연도 행 */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '0 4px' }}>
                      <button onClick={() => setPickerYear(y => y - 1)} style={{ width: 24, height: 24, border: 'none', background: 'transparent', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#45464d', padding: 0 }}>
                        <Icon name="chevron_left" size={16} />
                      </button>
                      <input type="number" value={pickerYear} onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) setPickerYear(v); }}
                        style={{ width: 80, fontSize: 16, fontWeight: 700, color: '#0b1c30', textAlign: 'center', border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', padding: 0, MozAppearance: 'textfield' }} />
                      <button onClick={() => setPickerYear(y => y + 1)} style={{ width: 24, height: 24, border: 'none', background: 'transparent', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#45464d', padding: 0 }}>
                        <Icon name="chevron_right" size={16} />
                      </button>
                    </div>
                    {/* 월 그리드 4x3 */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                      {Array.from({ length: 12 }, (_, i) => i).map(monthIdx => {
                        const isCurrent = pickerYear === month.getFullYear() && monthIdx === month.getMonth();
                        const isToday = pickerYear === today.getFullYear() && monthIdx === today.getMonth();
                        return (
                          <button key={monthIdx} onClick={() => selectMonth(pickerYear, monthIdx)}
                            style={{
                              padding: '10px 6px',
                              background: isCurrent ? '#e5eeff' : 'transparent',
                              color: isCurrent ? '#00668a' : '#45464d',
                              border: isToday && !isCurrent ? '1px solid #00668a' : '1px solid transparent',
                              borderRadius: 8, cursor: 'pointer',
                              fontSize: 13, fontWeight: isCurrent ? 700 : 500,
                              transition: 'all 0.1s'
                            }}
                            onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = '#F1F5F9'; }}
                            onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}>
                            {monthIdx + 1}월
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            <button onClick={nextMonth} style={{ width: 32, height: 32, border: '1px solid #c6c6cd', borderRadius: 8, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><Icon name="chevron_right" size={18} /></button>
            <button onClick={onClose} style={{ width: 32, height: 32, border: 'none', background: 'transparent', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#76777d', marginLeft: 4 }}><Icon name="close" size={20} /></button>
          </div>
        </div>

        {/* Filter row — 이벤트 타입 토글 */}
        <div style={{ padding: '10px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0, background: '#fafbff' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#76777d', letterSpacing: '0.04em' }}>필터</span>
          {[
            { key: 'inbound', label: '입고', bar: '#009844', bg: '#dcfce7', count: monthlyStats.inbound },
            { key: 'outbound', label: '출고', bar: '#ba1a1a', bg: '#ffebee', count: monthlyStats.outbound },
            { key: 'rma', label: 'RMA/DOA', bar: '#d84315', bg: '#fbe9e7', count: monthlyStats.rma },
            { key: 'cancel', label: '취소', bar: '#e65100', bg: '#fff4e5', count: monthlyStats.cancel },
            { key: 'return', label: '환불', bar: '#6a1b9a', bg: '#f3e5f5', count: monthlyStats.return },
          ].map(t => {
            const active = activeTypes.has(t.key);
            return (
              <button key={t.key} onClick={() => toggleType(t.key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '5px 12px',
                  borderRadius: 9999,
                  border: `1px solid ${active ? t.bar : '#c6c6cd'}`,
                  background: active ? t.bg : '#fff',
                  color: active ? t.bar : '#999',
                  fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                  opacity: active ? 1 : 0.55,
                  transition: 'all 0.15s',
                  textDecoration: active ? 'none' : 'line-through'
                }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? t.bar : '#bbb', flexShrink: 0 }} />
                {t.label}
                <strong style={{ marginLeft: 2 }}>{t.count}</strong>
              </button>
            );
          })}
          {!allActive && (
            <button onClick={resetTypes} style={{ padding: '5px 12px', borderRadius: 9999, border: '1px solid #c6c6cd', background: '#fff', color: '#45464d', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
              <Icon name="restart_alt" size={13} />
              초기화
            </button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#76777d' }}>
            {allActive ? `총 ${totalCount}건` : `${visibleCount}건 표시 (전체 ${totalCount}건)`}
          </span>
        </div>

        {/* Body — 캘린더 + 디테일 */}
        <div style={{ padding: 16, flex: 1, display: 'flex', gap: 16, minHeight: 0, overflow: 'hidden' }}>
          {/* 캘린더 (고정 폭) */}
          <div style={{ width: CALENDAR_WIDTH, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
            {/* 요일 헤더 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 6 }}>
              {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
                <div key={d} style={{ padding: '8px 10px', fontSize: 12, fontWeight: 700, color: i === 0 ? '#ba1a1a' : i === 6 ? '#00668a' : '#45464d', textAlign: 'left', letterSpacing: '0.04em' }}>{d}</div>
              ))}
            </div>
            {/* 셀 그리드 (6주 고정) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `repeat(6, ${CELL_HEIGHT}px)`, gap: 4, border: '1px solid #E2E8F0', borderRadius: 8, padding: 4, background: '#fafbff' }}>
              {cells.map((cell, i) => {
                const events = cell.fullDate ? (eventsByDate[cell.fullDate] || []).filter(isEventActive) : [];
                const isToday = cell.fullDate === todayStr;
                const isSelected = cell.fullDate === selectedDate;
                const dow = i % 7;
                return (
                  <div key={i} onClick={() => handleDayClick(cell)} style={{
                    height: CELL_HEIGHT, padding: 6,
                    background: isSelected ? '#e5eeff' : isToday ? 'rgba(0,102,138,0.06)' : cell.inCurrentMonth ? '#fff' : 'rgba(0,0,0,0.02)',
                    borderRadius: 6,
                    cursor: cell.inCurrentMonth ? 'pointer' : 'default',
                    transition: 'background 0.15s',
                    opacity: cell.inCurrentMonth ? 1 : 0.45,
                    border: isSelected ? '1.5px solid #00668a' : isToday ? '1px solid #00668a' : '1px solid transparent',
                    overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    boxSizing: 'border-box'
                  }}>
                    <div style={{ fontSize: 13, fontWeight: isToday ? 700 : 500, color: dow === 0 ? '#ba1a1a' : dow === 6 ? '#00668a' : '#45464d', marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                      <span>{cell.date}</span>
                      {events.length > 0 && cell.inCurrentMonth && <span style={{ fontSize: 9, fontWeight: 600, color: '#76777d', background: '#fff', padding: '0 4px', borderRadius: 3, border: '1px solid #E2E8F0' }}>{events.length}</span>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0, overflow: 'hidden' }}>
                      {events.slice(0, 4).map((ev, j) => {
                        const c = getEventColor(ev);
                        // v7.188: 여러 품목 요약 표기 — 대표 모델 + 외 N종 + 총 수량
                        const items = getEventItems(ev);
                        const totalQty = getEventTotalQty(items);
                        const first = items[0]?.name || '-';
                        const summary = items.length === 1
                          ? first
                          : `${first} 외 ${items.length - 1}종`;
                        const qtyLabel = totalQty > 0 ? `×${totalQty}` : '';
                        const tooltip = items.map(it => it.qty != null ? `${it.name} ×${it.qty}` : it.name).join(' / ');
                        return (
                          <div key={j} title={`${c.label} · ${tooltip}`} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '1px 5px', background: c.bg, borderLeft: `3px solid ${c.bar}`, borderRadius: 3, fontSize: 10, lineHeight: 1.3, overflow: 'hidden', flexShrink: 0 }}>
                            <span style={{ fontWeight: 700, color: c.bar, flexShrink: 0, fontSize: 9 }}>{c.label}</span>
                            <span style={{ color: '#0b1c30', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{summary}</span>
                            {qtyLabel && <span style={{ color: '#45464d', fontWeight: 700, flexShrink: 0, fontSize: 9 }}>{qtyLabel}</span>}
                          </div>
                        );
                      })}
                      {events.length > 4 && <div style={{ fontSize: 10, color: '#00668a', padding: '1px 5px', fontWeight: 600, flexShrink: 0 }}>+{events.length - 4} 더보기</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 디테일 패널 (선택 시) */}
          {selectedDate && (
            <div style={{ width: DETAIL_WIDTH, flexShrink: 0, border: '1px solid #E2E8F0', borderRadius: 8, padding: 14, overflowY: 'auto', background: '#fafbff' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#00668a' }}>{selectedDate.replace(/-/g, '.')}</div>
                <button onClick={() => setSelectedDate(null)} style={{ width: 24, height: 24, border: 'none', background: 'transparent', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#76777d', padding: 0 }}><Icon name="close" size={16} /></button>
              </div>
              {/* onPickDate 모드(배송시트지)에서만 "이 날짜로 보기" 점프 버튼 노출 */}
              {onPickDate && (
                <button onClick={() => {
                  const [y, m, d] = selectedDate.split('-').map(Number);
                  onPickDate(new Date(y, m - 1, d));
                  onClose && onClose();
                }} style={{ width: '100%', padding: '8px 12px', marginBottom: 12, background: '#00668a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Icon name="arrow_forward" size={14} /> 이 날짜로 보기
                </button>
              )}
              {selectedDateEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 36, color: '#76777d', fontSize: 13 }}>이 날짜에 이벤트가 없습니다.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 11, color: '#76777d', marginBottom: 2 }}>총 {selectedDateEvents.length}건</div>
                  {selectedDateEvents.map((ev, i) => {
                    const c = getEventColor(ev);
                    let parsedNote = null;
                    try { parsedNote = JSON.parse(ev.note); } catch (e) { }
                    // v7.188: 모든 품목 + 수량 표시 (items 배열이 있으면 전개, 없으면 fallback ev.model)
                    const items = getEventItems(ev);
                    const totalQty = getEventTotalQty(items);
                    return (
                      <div key={i} style={{ padding: 10, background: '#fff', borderLeft: `4px solid ${c.bar}`, borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5, gap: 8 }}>
                          <span style={{ padding: '2px 8px', fontSize: 10, fontWeight: 700, color: c.bar, background: c.bg, borderRadius: 4 }}>{c.label}</span>
                          <span style={{ fontSize: 10, color: '#76777d', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'right' }}>{ev.serial}</span>
                        </div>
                        {/* v7.188: items 리스트 — 각 품목의 제품명 · 수량 */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 4 }}>
                          {items.map((it, k) => (
                            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#0b1c30', wordBreak: 'break-all' }}>
                              <span style={{ flex: 1, minWidth: 0 }}>{it.name}</span>
                              {it.qty != null && <span style={{ fontSize: 11, fontWeight: 700, color: c.bar, background: c.bg, padding: '1px 7px', borderRadius: 9999, flexShrink: 0 }}>×{it.qty}</span>}
                            </div>
                          ))}
                          {items.length > 1 && (
                            <div style={{ fontSize: 10, color: '#76777d', textAlign: 'right', fontWeight: 700, marginTop: 1 }}>합계 ×{totalQty}</div>
                          )}
                        </div>
                        {ev.dealer_name && <div style={{ fontSize: 11, color: '#45464d', marginTop: 2 }}>· 딜러/수령인: <strong>{ev.dealer_name}</strong></div>}
                        {parsedNote?.method && <div style={{ fontSize: 11, color: '#45464d', marginTop: 2 }}>· 방식: {parsedNote.method}</div>}
                        {parsedNote?.requester && <div style={{ fontSize: 11, color: '#45464d', marginTop: 2 }}>· 요청자: {parsedNote.requester}</div>}
                        {ev.created_at && <div style={{ fontSize: 10, color: '#76777d', marginTop: 4 }}>{new Date(ev.created_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — 도움말 + 로딩 */}
        <div style={{ padding: '8px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', gap: 16, alignItems: 'center', fontSize: 11, color: '#76777d', flexWrap: 'wrap', background: '#fafbff', flexShrink: 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Icon name="touch_app" size={13} style={{ color: '#76777d' }} />
            날짜 클릭 → 상세 보기
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Icon name="keyboard" size={13} style={{ color: '#76777d' }} />
            ESC로 닫기
          </span>
          {loading && <span style={{ marginLeft: 'auto', color: '#00668a', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}><Icon name="progress_activity" size={12} style={{ animation: 'spin 1s linear infinite' }} />로딩 중...</span>}
        </div>
      </div>
    </div>
  );
}
